import { MOCK_CONFIG, generateMockFileName } from './mockConfig';

/**
 * Mock API Layer
 * Intercepts API requests and returns mock data from JSON files when available
 */

class MockApiService {
  constructor() {
    this.mockCache = new Map();
  }

  /**
   * Check if mock data should be used
   */
  shouldUseMock() {
    return MOCK_CONFIG.ENABLE_MOCK;
  }

  /**
   * Parse endpoint from full URL
   * @param {string} url - Full API URL
   * @returns {string} - Endpoint path
   */
  parseEndpoint(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname;
    } catch {
      // If URL parsing fails, assume it's already a path
      const queryIndex = url.indexOf('?');
      return queryIndex > -1 ? url.substring(0, queryIndex) : url;
    }
  }

  /**
   * Parse query parameters from URL
   * @param {string} url - Full API URL
   * @returns {object} - Query parameters object
   */
  parseQueryParams(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const params = {};
      urlObj.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return params;
    } catch {
      // Fallback to manual parsing
      const queryIndex = url.indexOf('?');
      if (queryIndex === -1) return {};
      
      const queryString = url.substring(queryIndex + 1);
      const params = {};
      queryString.split('&').forEach(param => {
        const [key, value] = param.split('=');
        if (key) params[decodeURIComponent(key)] = decodeURIComponent(value || '');
      });
      return params;
    }
  }

  /**
   * Load mock data from JSON file
   * @param {string} fileName - Mock data file name
   * @returns {Promise<any>} - Mock data
   */
  async loadMockData(fileName) {
    // Check cache first
    if (this.mockCache.has(fileName)) {
      return this.mockCache.get(fileName);
    }

    try {
      const response = await fetch(`${MOCK_CONFIG.MOCK_DATA_PATH}/${fileName}`);
      if (!response.ok) {
        throw new Error(`Mock file not found: ${fileName}`);
      }
      const data = await response.json();
      
      // Cache the data
      this.mockCache.set(fileName, data);
      return data;
    } catch (error) {
      if (MOCK_CONFIG.LOG_MOCK_USAGE) {
        console.warn(`[Mock API] Failed to load mock data from ${fileName}:`, error.message);
      }
      return null;
    }
  }

  /**
   * Get mock response for a request
   * @param {string} url - API endpoint URL
   * @returns {Promise<any|null>} - Mock data or null if not available
   */
  async getMockResponse(url) {
    if (!this.shouldUseMock()) {
      return null;
    }

    const endpoint = this.parseEndpoint(url);
    const queryParams = this.parseQueryParams(url);
    const fileName = generateMockFileName(endpoint, queryParams);

    if (!fileName) {
      if (MOCK_CONFIG.LOG_MOCK_USAGE) {
        console.log(`[Mock API] No mock configured for: ${endpoint}`);
      }
      return null;
    }

    if (MOCK_CONFIG.LOG_MOCK_USAGE) {
      console.log(`[Mock API] Loading mock data from: ${fileName} for ${endpoint}`);
    }

    // Simulate network delay
    if (MOCK_CONFIG.MOCK_DELAY > 0) {
      await new Promise(resolve => setTimeout(resolve, MOCK_CONFIG.MOCK_DELAY));
    }

    return await this.loadMockData(fileName);
  }

  /**
   * Clear mock data cache
   */
  clearCache() {
    this.mockCache.clear();
    if (MOCK_CONFIG.LOG_MOCK_USAGE) {
      console.log('[Mock API] Cache cleared');
    }
  }
}

// Export singleton instance
export const mockApiService = new MockApiService();

/**
 * Wrapper around BaseApiRequest that checks for mock data first
 * @param {AbortSignal} signal - Abort signal
 * @param {string} url - API endpoint URL
 * @param {string} apiCall - API identifier
 * @param {string} method - HTTP method
 * @param {any} body - Request body
 * @param {any} headers - Request headers
 * @param {Function} originalFetch - Original BaseApiRequest function
 * @param {boolean} downloadFile - Flag to indicate if the request is for downloading a file
 * @returns {Promise<any>} - API response or mock data
 */
export const mockApiRequest = async (signal, url, apiCall, method, body, headers, downloadFile, originalFetch) => {
  // Try to get mock data first
  const mockData = await mockApiService.getMockResponse(url);
  
  if (mockData !== null) {
    // Return mock data
    if (MOCK_CONFIG.LOG_MOCK_USAGE) {
      console.log(`[Mock API] Returning mock data for: ${url}`);
    }
    return Promise.resolve(mockData);
  }

  // No mock data available, use real API
  if (MOCK_CONFIG.LOG_MOCK_USAGE && MOCK_CONFIG.ENABLE_MOCK) {
    console.log(`[Mock API] No mock available, using real API for: ${url}`);
  }
  
  return originalFetch(signal, url, apiCall, method, body, headers, downloadFile);
};
